/*
 * Cubica product page (sections/cubica-main-product + cubica-related-products).
 *
 * <cubica-product-form>  option buttons → exact variant: hidden id, ?variant= in the URL,
 *                        price, availability, add button, variant photo and the mobile bar.
 *                        Photos tagged with a color or print position (data-color, data-position)
 *                        show only for that choice; untagged photos always show.
 *                        An option with one value ([data-single]) counts as already chosen.
 *                        Buy now (buy-now.tsx): fireworks, "Ya casi es tuyo", then /checkout
 *                        through Cubica.cart.buyNow; asks first when the bag holds other items.
 *                        Server-rendered <template data-*-for="variantId"> hold each
 *                        variant's price and availability, so money formatting stays in Liquid.
 * <cubica-related>       loads product recommendations through the Section Rendering API.
 */
(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = matchMedia('(min-width: 1024px)');
  const fill = (target, template) => target && template && target.replaceChildren(template.content.cloneNode(true));
  // "150.000 COP" like snippets/cubica-price (money_without_currency, dots for COP).
  const money = (cents, code) => {
    const cop = code === 'COP';
    const amount = new Intl.NumberFormat(cop ? 'es-CO' : 'en-US', { minimumFractionDigits: cop ? 0 : 2, maximumFractionDigits: cop ? 0 : 2 }).format(cents / 100);
    return `<span class="c-money">${amount} <span class="c-money__code">${code}</span></span>`;
  };

  /* Delivery date range, ported from the storefront's lib/util/delivery-window.ts:
     business days Monday to Friday in Bogotá, holidays skipped, orders after the cutoff ship next business day. */
  const DAY = 86400000;
  function deliveryWindow({ min, max, cutoff, holidays }, now = new Date()) {
    if (!(min >= 0 && max >= min && max <= 30 && cutoff >= 0 && cutoff <= 23)) return null;
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now).map(({ type, value }) => [type, value]));
    const today = Date.parse(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`);
    const off = new Set(holidays);
    const working = (t) => { const d = new Date(t).getUTCDay(); return d !== 0 && d !== 6 && !off.has(new Date(t).toISOString().slice(0, 10)); };
    const after = (t, n) => { while (n > 0) { t += DAY; if (working(t)) n--; } return t; };
    const dispatch = working(today) && Number(parts.hour) < cutoff ? today : after(today, 1);
    return [after(dispatch, min), after(dispatch, max)];
  }
  function showDelivery(root) {
    const el = root.querySelector('[data-delivery]');
    if (!el) return;
    const n = (key) => (el.dataset[key] === '' ? NaN : Number(el.dataset[key]));
    const range = deliveryWindow({ min: n('min'), max: n('max'), cutoff: n('cutoff'), holidays: el.dataset.holidays.split(',').map((d) => d.trim()).filter(Boolean) });
    if (!range) return;
    const date = new Intl.DateTimeFormat(document.documentElement.lang || 'es', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    el.textContent = el.dataset.arrives.replace('[start]', date.format(range[0])).replace('[end]', date.format(range[1]));
  }

  class CubicaProductForm extends HTMLElement {
    connectedCallback() {
      showDelivery(this);
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('[data-actions]');
      this.fieldsets = [...this.querySelectorAll('[data-option]')];
      const id = Number(this.querySelector('[data-variant-input]').value);
      const initial = this.variants.find((variant) => variant.id === id);
      this.state = this.fieldsets.map((fieldset, i) => (initial ? initial.options[i] : fieldset.dataset.single ?? null));
      this.buy = this.querySelector('[data-buy-now]');
      this.choice = this.querySelector('[data-buy-choice]');
      // Back from checkout through the back/forward cache: the button is no longer on its way.
      addEventListener('pageshow', (event) => event.persisted && this.buying && this.setBuying(false));
      this.defaultPrice = this.querySelector('[data-price]').innerHTML;
      this.soldOutId = this.querySelector('[id$="-sold-out"]')?.id;
      this.unavailableId = this.querySelector('[id$="-unavailable"]')?.id;

      this.addEventListener('click', (event) => {
        const value = event.target.closest('.c-pdp__value');
        if (value) return this.choose(value);
        const thumb = event.target.closest('[data-thumb]');
        if (thumb) return this.show(Number(thumb.dataset.thumb));
        if (event.target.closest('[data-bar-button]')) return this.barAction();
        if (event.target.closest('[data-buy-now]')) return this.startBuy();
        if (event.target.closest('[data-buy-only]')) return this.buyNow(true);
        if (event.target.closest('[data-buy-all]')) return this.buyNow(false);
        if (event.target.closest('[data-share]')) return navigator.share({ title: document.title, url: location.href }).catch(() => {});
        const copy = event.target.closest('[data-copy-link]');
        if (copy) this.copy(copy);
      });

      const share = this.querySelector('[data-share]');
      if (share && typeof navigator.share === 'function') share.hidden = false;

      this.watchSlides();
      this.watchActions();
      this.stick();
      this.update(false);
      desktop.addEventListener('change', () => {
        this.lead(null);
        this.update(false);
      });
    }

    choose(button) {
      const index = this.fieldsets.indexOf(button.closest('[data-option]'));
      this.state[index] = button.dataset.value;
      this.update(true);
    }

    get variant() {
      if (this.state.some((value) => value == null)) return null;
      return this.variants.find((variant) => variant.options.every((value, i) => value === this.state[i])) || null;
    }

    update(chosen) {
      const variant = this.variant;
      this.fieldsets.forEach((fieldset, index) => {
        const current = this.state[index];
        const label = fieldset.querySelector('[data-option-current]');
        if (label) label.textContent = current ? `: ${current}` : '';
        fieldset.querySelectorAll('.c-pdp__value').forEach((button) => {
          const value = button.dataset.value;
          button.setAttribute('aria-pressed', String(value === current));
          const matches = this.variants.filter(
            (v) => v.options[index] === value && v.options.every((o, i) => i === index || this.state[i] == null || o === this.state[i]),
          );
          const struck = !matches.some((v) => v.available);
          button.classList.toggle('is-struck', struck);
          const note = !struck ? null : matches.length ? this.soldOutId : this.unavailableId;
          if (note) button.setAttribute('aria-describedby', note);
          else button.removeAttribute('aria-describedby');
        });
      });

      this.querySelector('[data-variant-input]').value = variant ? variant.id : '';
      const price = this.querySelector('[data-price]');
      const availability = this.querySelector('[data-availability]');
      if (variant) {
        fill(price, this.querySelector(`[data-price-for="${variant.id}"]`));
        fill(availability, this.querySelector(`[data-availability-for="${variant.id}"]`));
        fill(this.querySelector('[data-bar-price]'), this.querySelector(`[data-bar-price-for="${variant.id}"]`));
      } else {
        price.innerHTML = this.defaultPrice;
        availability.textContent = '';
      }
      const barVariant = this.querySelector('[data-bar-variant]');
      if (barVariant) barVariant.textContent = variant && this.fieldsets.length ? ` · ${variant.title}` : '';

      const label = !variant ? this.dataset.choose : variant.available ? this.dataset.add : this.dataset.soldOut;
      const add = this.querySelector('[data-add-button]');
      add.textContent = label;
      add.disabled = !variant || !variant.available || !!this.buying;
      if (this.buy && !this.buying) this.buy.disabled = !variant || !variant.available;
      const bar = this.querySelector('[data-bar-button]');
      if (bar) {
        bar.textContent = label;
        bar.disabled = !!variant && !variant.available;
      }
      const status = this.querySelector('[data-status]');
      if (chosen && status) status.textContent = '';
      const first = this.filterSlides();
      const media = variant?.featured_media?.id;
      const index = media ? this.slides.findIndex((slide) => slide.dataset.mediaId === String(media) && !slide.hidden) : -1;
      const color = this.option('color');
      const target = index >= 0 ? index : this.slides.findIndex((slide) => color && slide.dataset.color === color);
      const moved = target !== this.target;
      this.target = target;

      if (!chosen) return;
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      if (!this.closest('dialog')) history.replaceState(history.state, '', url);
      if (desktop.matches) {
        if (!moved || target < 0) return;
        this.lead(media);
        const stack = this.querySelector('[data-slides]');
        if (stack.getBoundingClientRect().top < 0) stack.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      if (index >= 0) this.show(index);
      else if (this.slides[this.active ?? 0]?.hidden) {
        // Back to the first photo of the choice, sideways only, so the page does not jump.
        const track = this.querySelector('[data-slides]');
        track.scrollTo({ left: this.slides[first].offsetLeft - track.offsetLeft, behavior: reduced() ? 'auto' : 'smooth' });
      }
    }

    /* The chosen color and position's photos; without one for that pair, the color's photos; else every photo. Returns the first shown. */
    option(name) {
      return this.state[this.fieldsets.findIndex((fieldset) => fieldset.dataset.optionName === name)] ?? null;
    }

    lead(media) {
      const color = this.option('color');
      const rank = (slide) => (slide.dataset.mediaId === String(media) ? -2 : color && slide.dataset.color === color ? -1 : '');
      this.slides.forEach((slide) => {
        const order = desktop.matches ? rank(slide) : '';
        slide.style.order = order;
        const thumb = this.querySelector(`[data-thumb-for="${slide.id}"]`);
        if (thumb) thumb.style.order = order;
      });
    }

    filterSlides() {
      const fits = (slide, color, position) =>
        (!color || !slide.dataset.color || slide.dataset.color === color) && (!position || !slide.dataset.position || slide.dataset.position === position);
      const tagged = (slide) => slide.dataset.color || slide.dataset.position;
      const [color, position] = [[this.option('color'), this.option('posición')], [this.option('color'), null], [null, null]].find(
        ([c, p]) => (!c && !p) || this.slides.some((slide) => tagged(slide) && fits(slide, c, p)),
      );
      this.slides.forEach((slide) => {
        const hide = !desktop.matches && !fits(slide, color, position);
        slide.hidden = hide;
        this.querySelector(`[data-thumb-for="${slide.id}"]`)?.toggleAttribute('hidden', hide);
      });
      return Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    }

    barAction() {
      if (this.variant?.available) return this.form.requestSubmit();
      const first = this.fieldsets.find((_, i) => this.state[i] == null) || this.fieldsets[0];
      first?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      first?.querySelector('.c-pdp__value')?.focus({ preventScroll: true });
    }

    /* Buy now: with nothing else in the bag it goes straight on; otherwise the shopper chooses. */
    async startBuy() {
      const variant = this.variant;
      if (this.buying || !variant?.available || this.form.querySelector('[aria-busy="true"]')) return;
      const inBag = (document.querySelector('.c-bag__content')?.dataset.variants || '').split(',').filter(Boolean);
      if (inBag.every((id) => id === String(variant.id))) return this.buyNow(false);
      let cart;
      try {
        cart = await Cubica.cart.get();
      } catch {
        return this.buyFailed();
      }
      const others = cart.items.filter((item) => item.variant_id !== variant.id);
      if (!others.length) return this.buyNow(false);
      const has = others.length < cart.items.length;
      const count = cart.item_count + (has ? 0 : 1);
      const waiting = others.reduce((sum, item) => sum + item.quantity, 0);
      const copy = this.choice.dataset;
      this.choice.querySelector('[data-buy-choice-description]').textContent =
        waiting === 1 ? copy.one : copy.other.replace('[count]', waiting);
      fill(this.choice.querySelector('[data-buy-only-price]'), this.querySelector(`[data-bar-price-for="${variant.id}"]`));
      this.choice.querySelector('[data-buy-all-detail]').innerHTML = `${
        count === 1 ? copy.item : copy.items.replace('[count]', count)
      } · ${money(cart.total_price + (has ? 0 : variant.price), cart.currency)}`;
      Cubica.openDialog(this.choice.id, this.buy);
    }

    async buyNow(only) {
      const variant = this.variant;
      if (this.buying || !variant) return;
      if (this.choice?.open) this.choice.close();
      this.querySelector('[data-buy-now-error]').textContent = '';
      // Four quiet fireworks, alternating sides so the label stays clear; nearer the edges on phones.
      [[12, 8, 0], [88, 92, 120], [24, 17, 240], [76, 83, 360]].forEach(([wide, narrow, delay]) =>
        Cubica.burst(this.buy, { subtle: true, delay, left: (desktop.matches ? wide : narrow) + '%' }),
      );
      this.setBuying(true);
      try {
        await Cubica.cart.buyNow(variant.id, only);
        location.assign('/checkout'); // stays "Ya casi es tuyo" until checkout replaces the page
      } catch {
        this.buyFailed();
      }
    }

    buyFailed() {
      this.setBuying(false);
      this.querySelector('[data-buy-now-error]').textContent = this.buy.dataset.error;
    }

    // Busy, not disabled (HIG activity indicator): a spinner joins the new label, which rises in.
    setBuying(buying) {
      this.buying = buying;
      const label = this.buy.querySelector('[data-buy-now-label]');
      label.textContent = buying ? this.buy.dataset.busy : this.buy.dataset.label;
      if (buying) label.prepend(Object.assign(document.createElement('span'), { className: 'c-spinner' }));
      label.classList.remove('is-changing');
      void label.offsetWidth;
      label.classList.add('is-changing');
      for (const name of ['aria-busy', 'aria-disabled']) {
        if (buying) this.buy.setAttribute(name, 'true');
        else this.buy.removeAttribute(name);
      }
      this.querySelector('[data-buy-now-live]').textContent = buying ? this.buy.dataset.busy : '';
      this.update(false);
    }

    async copy(button) {
      const status = this.querySelector('[data-share-status]');
      try {
        await navigator.clipboard.writeText(location.href);
        status.textContent = button.dataset.copied;
      } catch {
        status.textContent = button.dataset.copyError;
      }
    }

    /* Gallery: thumbnails follow the photo in view; a thumbnail brings its photo. */
    watchSlides() {
      this.slides = [...this.querySelectorAll('[data-slides] > *')];
      const thumbs = [...this.querySelectorAll('[data-thumb]')];
      if (!thumbs.length) return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const active = (this.active = this.slides.indexOf(entry.target));
            thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === active)));
          }
        },
        { threshold: 0.6 },
      );
      this.slides.forEach((slide) => observer.observe(slide));
    }

    show(index) {
      this.slides[index]?.scrollIntoView({
        behavior: reduced() ? 'auto' : 'smooth',
        block: desktop.matches ? 'start' : 'nearest',
        inline: 'center',
      });
    }

    /* Mobile bar only while the buy box is off screen (NN/g sticky elements). */
    watchActions() {
      const bar = this.querySelector('[data-bar]');
      if (!bar) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
      }).observe(this.form);
    }

    /* Sticky column with no scroll of its own: when taller than the screen it
       scrolls with the page until its end is in view, then stays. */
    stick() {
      const column = this.querySelector('[data-sticky]');
      if (!column) return;
      const update = () => (column.style.top = `${Math.min(64, innerHeight - column.offsetHeight - 16)}px`);
      new ResizeObserver(update).observe(column);
      addEventListener('resize', update);
    }
  }

  class CubicaRelated extends HTMLElement {
    connectedCallback() {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' },
      );
      observer.observe(this);
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-rail-step]');
        if (!step) return;
        const track = this.querySelector('[data-rail]');
        track.scrollBy({ left: Number(step.dataset.railStep) * track.clientWidth * 0.8, behavior: reduced() ? 'auto' : 'smooth' });
      });
    }
    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector('cubica-related');
        if (next && next.children.length) this.replaceChildren(...next.childNodes);
      } catch {}
    }
  }

  customElements.define('cubica-product-form', CubicaProductForm);
  customElements.define('cubica-related', CubicaRelated);
})();
